import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Plus, Sparkles, Trash2, FileText, PenLine } from "lucide-react";
import { useFamily } from "../context/FamilyContext";
import { api, errMsg, fmtDate, firstName } from "../lib/api";
import { PageTitle, Loading } from "../components/Bits";

const SECTIONS = [
  ["will", "What the helper WILL do"],
  ["wont", "What the helper will NOT do"],
  ["house_rules", "House rules"],
  ["check_in", "How and when to check in"],
  ["emergency", "What counts as an emergency"],
  ["schedule", "Hours and schedule"],
  ["feedback", "How to give feedback"],
];

const uid = () => `it_${Math.random().toString(36).slice(2, 10)}`;

function Confirm({ on, label, can, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      disabled={!can}
      onClick={onClick}
      className={`inline-flex min-h-[40px] items-center gap-1 rounded-full px-3 text-sm font-semibold transition-colors ${
        on ? "bg-hf-green text-hf-cream" : "border-2 border-hf-line bg-white text-hf-muted"
      } ${can ? "" : "opacity-70"}`}
    >
      {on && <Check className="h-4 w-4" />} {label}
    </button>
  );
}

function SectionEditor({ k, title, items, update, names, roles, fid }) {
  const [text, setText] = useState("");
  const [ideas, setIdeas] = useState([]);
  const [busy, setBusy] = useState(false);
  const add = (t) => {
    if (!t.trim()) return;
    update([...items, { id: uid(), text: t.trim(), caregiver_ok: false, helper_ok: false }]);
    setText("");
    setIdeas((xs) => xs.filter((x) => x !== t));
  };
  const suggest = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/families/${fid}/agreement/suggest`, { section: k });
      setIdeas(r.data.items);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const toggle = (id, role) => update(items.map((it) => (it.id === id ? { ...it, [`${role}_ok`]: !it[`${role}_ok`] } : it)));
  return (
    <section data-testid={`agreement-section-${k}`} className="hf-card">
      <h2 className="font-heading text-xl font-semibold text-hf-green">{title}</h2>
      <ul className="mt-4 space-y-4">
        {items.map((it) => (
          <li key={it.id} data-testid={`agreement-item-${it.id}`} className="rounded-xl bg-hf-cream p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="text-lg leading-snug">{it.text}</p>
              <button aria-label="Remove" data-testid={`agreement-remove-${it.id}`} onClick={() => update(items.filter((x) => x.id !== it.id))} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-hf-muted hover:bg-white">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Confirm on={it.caregiver_ok} label={names.caregiver} can={roles.includes("caregiver")} onClick={() => toggle(it.id, "caregiver")} testid={`confirm-caregiver-${it.id}`} />
              <Confirm on={it.helper_ok} label={names.helper} can={roles.includes("helper")} onClick={() => toggle(it.id, "helper")} testid={`confirm-helper-${it.id}`} />
            </div>
          </li>
        ))}
      </ul>
      {ideas.length > 0 && (
        <div className="mt-4 space-y-2" data-testid={`agreement-ideas-${k}`}>
          <p className="text-sm font-semibold uppercase tracking-wider text-hf-muted">Ideas. Add what fits.</p>
          {ideas.map((t, i) => (
            <button key={t} data-testid={`agreement-idea-${k}-${i}`} onClick={() => add(t)} className="flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-hf-line bg-white px-4 py-3 text-left text-base hover:border-hf-green">
              <Plus className="h-5 w-5 shrink-0 text-hf-coral" /> {t}
            </button>
          ))}
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <input data-testid={`agreement-add-input-${k}`} className="hf-input" placeholder="Add your own" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add(text)} />
        <button data-testid={`agreement-add-button-${k}`} aria-label="Add" onClick={() => add(text)} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-hf-green text-hf-cream">
          <Plus className="h-6 w-6" />
        </button>
      </div>
      <button data-testid={`agreement-suggest-${k}`} disabled={busy} onClick={suggest} className="mt-3 inline-flex min-h-[44px] items-center gap-2 text-base font-semibold text-hf-green">
        <Sparkles className="h-4 w-4 text-hf-coral" /> {busy ? "Thinking..." : "Suggest common items"}
      </button>
    </section>
  );
}

function OnePage({ a, names }) {
  return (
    <article data-testid="agreement-one-page" className="hf-card">
      <p className="hf-eyebrow">Expectations Agreement · Version {a.version}</p>
      <h2 className="mt-1 font-heading text-2xl font-semibold">{names.caregiver} and {names.helper}</h2>
      {SECTIONS.map(([k, t]) => (
        <div key={k} className="mt-5 border-t border-hf-line pt-4">
          <h3 className="font-heading text-lg font-semibold text-hf-green">{t}</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-lg">
            {a.sections[k].map((it) => <li key={it.id}>{it.text}</li>)}
            {!a.sections[k].length && <li className="list-none pl-0 text-hf-muted">Nothing yet.</li>}
          </ul>
        </div>
      ))}
      <p className="mt-6 text-base text-hf-muted">
        {a.agreed.caregiver ? `${names.caregiver} agreed ${fmtDate(a.agreed.caregiver)}. ` : ""}
        {a.agreed.helper ? `${names.helper} agreed ${fmtDate(a.agreed.helper)}.` : ""}
      </p>
      {a.history?.length > 0 && (
        <div className="mt-4 space-y-1 text-sm text-hf-muted">
          {a.history.map((h, i) => <p key={i}>v{h.version} · {fmtDate(h.at)} · {h.note}</p>)}
        </div>
      )}
    </article>
  );
}

export default function Agreement() {
  const { fid, data, roles, helper, refresh } = useFamily();
  const [a, setA] = useState(null);
  const [view, setView] = useState("edit");
  const timer = useRef(null);
  const names = { caregiver: firstName(data.family.caregiver_name) || "Caregiver", helper: firstName(helper?.name) || "Helper" };

  useEffect(() => {
    api.get(`/families/${fid}/agreement`).then((r) => {
      setA(r.data);
      if (r.data.agreed.caregiver && r.data.agreed.helper) setView("page");
    });
  }, [fid]);

  const update = (k) => (items) => {
    const next = { ...a, sections: { ...a.sections, [k]: items } };
    setA(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.put(`/families/${fid}/agreement`, { sections: next.sections });
        setA((cur) => ({ ...cur, agreed: r.data.agreed, version: r.data.version }));
      } catch (e) {
        toast.error(errMsg(e, "Couldn't save. We'll keep trying."));
      }
    }, 600);
  };

  const agree = async (role) => {
    clearTimeout(timer.current);
    try {
      await api.put(`/families/${fid}/agreement`, { sections: a.sections });
      const r = await api.post(`/families/${fid}/agreement/agree`, { role });
      setA(r.data);
      if (r.data.agreed.caregiver && r.data.agreed.helper) {
        toast.success("Agreed by both. Saved.");
        setView("page");
        refresh();
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!a) return <Loading />;
  const both = a.agreed.caregiver && a.agreed.helper;
  const needsUpdate = data.reports.some((r) => r.needs_agreement_update);

  return (
    <div className="space-y-6">
      <PageTitle eyebrow="Expectations Agreement" title="Most respite falls apart because nothing was written down. Let's write it down." />
      {needsUpdate && (
        <p data-testid="agreement-update-nudge" className="rounded-2xl bg-hf-sun/30 p-5 text-lg">
          A recent Relationship Check suggested a change. Take a few minutes together to update this.
        </p>
      )}
      <div className="flex items-center justify-between gap-3">
        <p data-testid="agreement-status" className="text-base font-medium text-hf-muted">
          Version {a.version} · {both ? "Agreed by both" : "Not agreed yet"}
        </p>
        <button data-testid="agreement-view-toggle" onClick={() => setView(view === "edit" ? "page" : "edit")} className="inline-flex min-h-[44px] shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-white px-4 text-base font-semibold text-hf-green ring-1 ring-hf-line">
          {view === "edit" ? <><FileText className="h-4 w-4" /> One page</> : <><PenLine className="h-4 w-4" /> Edit together</>}
        </button>
      </div>
      {view === "page" ? (
        <OnePage a={a} names={names} />
      ) : (
        <>
          <p className="text-lg text-hf-muted">Fill this in together. Each of you taps your name to confirm an item.</p>
          {SECTIONS.map(([k, t]) => (
            <SectionEditor key={k} k={k} title={t} items={a.sections[k]} update={update(k)} names={names} roles={roles} fid={fid} />
          ))}
          <div className="hf-card space-y-3">
            <h2 className="font-heading text-xl font-semibold">Ready?</h2>
            {["caregiver", "helper"].map((role) => (
              <button
                key={role}
                data-testid={`agreement-agree-${role}`}
                disabled={!roles.includes(role) || !!a.agreed[role]}
                onClick={() => agree(role)}
                className={`hf-btn w-full ${a.agreed[role] ? "bg-hf-sage text-hf-green" : "hf-btn-primary"}`}
              >
                {a.agreed[role] ? <><Check className="h-5 w-5" /> {names[role]} agreed</> : `${names[role]}: Agree`}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
